import React, { useMemo } from "react";
import { useQuery } from "@apollo/client";
import { Box, Stack, Switch, Text } from "@chakra-ui/react";
import { CreatableSelect } from "chakra-react-select";

import { GET_RECRUITER_USERS_QUERY } from "../graphql/agencies";

// The job lists filter on one contact email, sent to the server as the
// `contactEmail` query variable. "My jobs" is not a separate filter, just a
// shortcut for "the contact email is mine", so the toggle and the select can
// never disagree.

const normalizeEmail = (value) =>
  typeof value === "string" ? value.trim() : "";

export const sameEmail = (a, b) => {
  const left = normalizeEmail(a).toLowerCase();
  return left !== "" && left === normalizeEmail(b).toLowerCase();
};

const recruiterLabel = (recruiter) => {
  const name =
    recruiter.fullName ||
    [recruiter.firstName, recruiter.lastName].filter(Boolean).join(" ");
  return name ? `${name} <${recruiter.email}>` : recruiter.email;
};

// Loose on purpose: the server matches exactly, so this only stops obvious
// typos (a bare name) from being offered as a filter.
const looksLikeEmail = (input) => /^[^\s@]+@[^\s@]+$/.test(normalizeEmail(input));

function JobContactEmailFilter({ value, onChange, currentUserEmail }) {
  const emit = (next) => onChange(normalizeEmail(next) || null);

  // The agency's recruiters seed the options. If the list cannot be loaded
  // the select still takes a typed email, so an error just means no options.
  const { data } = useQuery(GET_RECRUITER_USERS_QUERY);

  const options = useMemo(() => {
    const seen = new Set();
    const list = [];
    (data?.getRecruiterUsers || []).forEach((recruiter) => {
      const email = normalizeEmail(recruiter?.email);
      if (!email || seen.has(email.toLowerCase())) return;
      seen.add(email.toLowerCase());
      list.push({ value: email, label: recruiterLabel(recruiter) });
    });
    return list.sort((a, b) => a.label.localeCompare(b.label));
  }, [data]);

  // A typed email that is not a recruiter still has to show as
  // the current selection.
  const selected = useMemo(() => {
    if (!value) return null;
    return (
      options.find((option) => sameEmail(option.value, value)) || {
        value,
        label: value,
      }
    );
  }, [options, value]);

  const isMine = sameEmail(value, currentUserEmail);

  return (
    <Stack
      direction={{ base: "column", sm: "row" }}
      spacing={3}
      align={{ base: "stretch", sm: "center" }}
      w={{ base: "full", md: "auto" }}
    >
      <Switch
        colorScheme="teal"
        isChecked={isMine}
        isDisabled={!currentUserEmail}
        onChange={(e) => emit(e.target.checked ? currentUserEmail : null)}
        display="flex"
        alignItems="center"
        flexShrink={0}
      >
        <Text as="span" color="white" whiteSpace="nowrap">
          My jobs
        </Text>
      </Switch>
      <Box w={{ base: "full", sm: "280px" }}>
        <CreatableSelect
          aria-label="Contact email"
          inputId="job-contact-email-filter"
          placeholder="Contact email"
          isClearable
          options={options}
          value={selected}
          onChange={(option) => emit(option?.value)}
          isValidNewOption={looksLikeEmail}
          formatCreateLabel={(input) => `Filter by ${normalizeEmail(input)}`}
          // Trim the filter value; the menu row keeps the "Filter by" label.
          getNewOptionData={(input, createLabel) => ({
            value: normalizeEmail(input),
            label: createLabel,
            __isNew__: true,
          })}
          chakraStyles={{
            container: (provided) => ({ ...provided, width: "100%" }),
          }}
        />
      </Box>
    </Stack>
  );
}

export default JobContactEmailFilter;
